import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import { Page } from 'src/app/shared/models/page';
import { Observable, from } from 'rxjs';
import { PagedData, PagedDataUserMenu } from 'src/app/shared/models/paged-data';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { PoTokenAuthService } from 'src/app/shared/services/po-token-auth.service';
import { RfqTokenAuthService } from 'src/app/shared/services/rfq-token-auth.service';
import { menuPageParams, PageParams } from 'src/app/shared/common/Enums';
import { map } from 'rxjs/operators';
import { ApiUrlConstants } from 'src/app/shared/common/ApiUrlConstants';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import {
  AddUserResponse,
  ChangeUserPasswordForm,
  ForgotPassForm,
  ForgotPasswordResponse,
  LoginForm,
  LoginResponse,
  NewPassAfterForgotPassForm,
  ResetPassForm,
  SignUpForm,
} from 'src/app/shared/models/user.model';
import { UserCategory } from 'src/app/shared/models/user-category.model';
import { UserMenu } from 'src/app/shared/models/user-menu.model';
import { UserMenuPage } from 'src/app/shared/models/user-menu-page';
import {
  AccountDataType,
  Category,
  Currency,
  AutoCompleteDataType,
  aboutDataType,
  supplierBranchData,
  supplierCatalogueProduct,
  supplierPortData,
  informationType,
  Brand,
  ImpaDataType,
  MtmlUom,
  ServiceApprovalClass,
  Vessel,
  AutoCompleteVesselFilterDataType,
  buyerAboutDataType,
  AutoCompleteSupplierProduct,
} from 'src/app/shared/models/settings.model';

export interface UserCookieObj {
  name: string;
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  constructor(
    private webApiService: WebApiService,
    private cookieService: CookieService,
    private router: Router,
    private fullLoaderService: FullLoaderService,
    private navigationService: NavigationService,
    private snack: MatSnackBar,
    private poTokenAuth: PoTokenAuthService,
    private rfqTokenAuth: RfqTokenAuthService
  ) {}

  /**
   * Save/Update the user's profile photo URL.
   * Sends the uploaded S3 URL to backend to persist in DB.
   */
  saveUserProfilePhotoUrl(userId: number, photoUrl: string): Promise<any> {
    const payload = { userId, photoUrl };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateProfilePhotoUrl,
      payload
    );
  }

  getGeolocation(geoAddress: string): Promise<any> {
    const address = { geoAddress };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getGeoLocation,
      address
    );
  }

  fetchAllCountryDetails():Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllCountryDetails
    );
  }

  fetchAllCountryDetailsByRfqToken():Promise<any> {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.getRequestWithOptions<any>(
      ApiUrlConstants.getAllCountryDetailsByRfqToken,
      options
    );
  }

  addIncotermsData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addIncotermsData,
      reqBody
    );
  }

  addModeOfTransport(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addModeOfTransport,
      reqBody
    );
  }

  addModeOfTransportToFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addModeOfTransportToFreightForwarder,
      reqBody
    );
  }

  fetchModeofTransportByUserId(userId: number):Promise<any> {
     const url = `${ApiUrlConstants.getModeofTransportByUserId}/${userId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllModeOfTransport(): Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllModeOfTransport
    );
  }

  addPackaging(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPackaging,
      reqBody
    );
  }

  addPackagingToFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPackagingToFreightForwarder,
      reqBody
    );
  }

  fetchPackagingByUserId(userId: number):Promise<any> {
     const url = `${ApiUrlConstants.getPackagingByUserId}/${userId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllPackaging(): Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllPackaging
    );
  }
  
  fetchAllIncotermsBySupplierId(supplierId: number):Promise<any> {
     const url = `${ApiUrlConstants.getAllIncotermsBySupplierId}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllIncotermsBySupplierIdByRfqToken():Promise<any> {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.getRequestWithOptions<any>(
      ApiUrlConstants.getAllIncotermsBySupplierIdByRfqToken,
      options
    );
  }

  fetchSupplierPaymentDetails(supplierId: number):Promise<any> {
     const url = `${ApiUrlConstants.fetchSupplierPaymentDetails}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  addPaymentDetails(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPaymentDetails,
      reqBody
    );
  }

  updatePaymentDetails(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.updatePaymentDetails,
      reqBody
    );
  }

  getAccountData(): Promise<AccountDataType> {
    return this.webApiService.getRequest<AccountDataType>(
      ApiUrlConstants.getAccountData
    );
  }


  getAccountDataById(supplierId: number): Promise<AccountDataType> {
    const url = `${ApiUrlConstants.getAccountData}/${supplierId}`;
    return this.webApiService.getRequest<AccountDataType>(url);
  }

  getAboutData(): Promise<aboutDataType> {
    return this.webApiService.getRequest<aboutDataType>(
      ApiUrlConstants.getAboutData
    );
  }
  // to fetch about data of specific supplier
  getAboutDataById(supplierId: number): Promise<aboutDataType> {
    const url = `${ApiUrlConstants.getAboutData}/${supplierId}`;
    return this.webApiService.getRequest<aboutDataType>(url);
  }

  getBuyerAboutData(): Promise<buyerAboutDataType> {
    return this.webApiService.getRequest<buyerAboutDataType>(
      ApiUrlConstants.getBuyerAboutData
    );
  }
  // to fetch about data of specific supplier
  getBuyerAboutDataById(buyerId: number): Promise<buyerAboutDataType> {
    const url = `${ApiUrlConstants.getBuyerAboutData}/${buyerId}`;
    return this.webApiService.getRequest<buyerAboutDataType>(url);
  }

  getSupplierPorts(_params: any): Promise<supplierPortData> {
    return this.webApiService.postRequest<supplierPortData>(
      ApiUrlConstants.getSupplierPortData,
      _params
    );
  }

  getSupplierPortsById(_params: any): Promise<supplierPortData> {
    return this.webApiService.postRequest<supplierPortData>(
      ApiUrlConstants.getSupplierPortDataById,
      _params
    );
  }

  getSupplierBranches(usertype: string): Promise<supplierBranchData[]> {
    const url = `${ApiUrlConstants.getSupplierBranchData}/${usertype}`;
    return this.webApiService.getRequest<supplierBranchData[]>(url);
  }

  getSupplierBranchesById(
    supplierId: number,
    usertype: string
  ): Promise<supplierBranchData[]> {
    const url = `${ApiUrlConstants.getSupplierBranchData}/${usertype}/${supplierId}`;
    return this.webApiService.getRequest<supplierBranchData[]>(url);
  }

  getAllCatalogues(): Promise<supplierCatalogueProduct[]> {
    return this.webApiService.getRequest<supplierCatalogueProduct[]>(
      ApiUrlConstants.supplierCatalogue
    );
  }

  getAllCataloguesById(
    supplierId: number
  ): Promise<supplierCatalogueProduct[]> {
    const url = `${ApiUrlConstants.supplierCatalogue}/${supplierId}`;
    return this.webApiService.getRequest<supplierCatalogueProduct[]>(url);
  }

  getAllProducts(
    searchTerm: string,
    pageMetaData: Page,
    isImpa?: boolean,
    category?: boolean
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set('category', category || false)
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set(PageParams.isImpa, isImpa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(ApiUrlConstants.products, params)
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages,
            _pageMetadata.size,
            paginatedMaster.count
          );
          // console.log('PaginatedMaster Data-> ',paginatedMaster);
          return paginatedMaster;
        })
      );
  }

  getBulkAttachmentUrls(body: any): Promise<any> {
    // Create a deep copy of the request body to avoid modifying the original
    const reqBody = JSON.parse(JSON.stringify(body));
    // remove the data url to reduce the size of the request body
    reqBody.map((attachment: any) => {
      delete attachment.file;
      delete attachment.dataUrl;
    });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getBulkProductAttachmentUrls,
      reqBody
    );
  }

  getBulkAttachmentUrlsByRfqToken(body: any): Promise<any> {
    const reqBody = JSON.parse(JSON.stringify(body));
    reqBody.map((attachment: any) => {
      delete attachment.file;
      delete attachment.dataUrl;
    });
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getBulkProductAttachmentUrlsByRfqToken,
      reqBody,
      options
    );
  }

  addProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createProduct,
      reqBody
    );
  }

  addMySupplierProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addMySupplierProduct,
      reqBody
    );
  }
  validateProductName(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.validateProductName,
      reqBody
    );
  }

  deleteProduct(productId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.products}/${productId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  deleteSupplierProduct(productId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.supplierProduct}/${productId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  deleteSupplierService(productId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.supplierService}/${productId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  assignProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignProduct,
      reqBody
    );
  }

  assignService(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignService,
      reqBody
    );
  }

  addCategory(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createCategory,
      reqBody
    );
  }

  updateCategory(reqBody: any, categoryId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.updateCategory}/${categoryId}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  fetchAllProductCategory(
    filterValue: string,
    limit: number,
    offset: number
  ): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.searchParam, filterValue)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllProductCategory,
      params
    );
  }

  fetchAllArchivedProductCategory(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchArchivedProductCategory,
      params
    );
  }

  fetchAllServiceCategory(
    filterValue: string,
    limit: number,
    offset: number
  ): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false)
      .set(PageParams.searchParam, filterValue);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllServiceCategory,
      params
    );
  }

  fetchArchivedServiceCategory(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchArchivedServiceCategory,
      params
    );
  }

  fetchAllPendingProductCategory(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllPendingProductCategory,
      params
    );
  }

  fetchAllPendingServiceCategory(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllPendingServiceCategory,
      params
    );
  }

  deleteCategory(categoryId: number) {
    let apiUrl = `${ApiUrlConstants.fetchAllProductCategory}/${categoryId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  restoreArchivedCategory(categoryId: number) {
    let apiUrl = `${ApiUrlConstants.restoreArchivedCategory}/${categoryId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  approveCategory(categoryId: number) {
    let apiUrl = `${ApiUrlConstants.approveCategory}/${categoryId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  addBrand(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createBrand,
      reqBody
    );
  }   
  addUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addUomMtmlUomData,
      reqBody
    );
  }   
  addPortByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPortByAdmin,
      reqBody
    );
  }   
  importPortsByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importPortsByAdmin,
      reqBody
    );
  }
  updatePortByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.updatePortByAdmin,
      reqBody
    );
  }  

  importUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importUomMtmlUomData,
      reqBody
    );
  }

  updateUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.updateUomMtmlUomData,
      reqBody
    );
  }  

  restorePortByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.restorePortByAdmin,
      reqBody
    );
  } 

  archiveOrUnarchiveUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.archiveOrUnarchiveUomMtmlUomData,
      reqBody
    );
  } 



  updateBrand(reqBody: any, brandId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.updateBrand}/${brandId}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  fetchAllBrand(
    filterValue: string,
    limit: number,
    offset: number
  ): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.searchParam, filterValue)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllBrand,
      params
    );
  }

  fetchAllArchivedbrand(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchArchivedBrand,
      params
    );
  }

  fetchAllPendingbrand(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllPendingBrand,
      params
    );
  }

  deleteBrand(brandId: number) {
    let apiUrl = `${ApiUrlConstants.fetchAllBrand}/${brandId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  restoreArchivedBrand(brandId: number) {
    let apiUrl = `${ApiUrlConstants.restoreArchivedBrand}/${brandId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  approveBrand(brandId: number) {
    let apiUrl = `${ApiUrlConstants.approveBrand}/${brandId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  updateProduct(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.products}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteAttachment(attachmentArr: number[]): Promise<any> {
    const attachments = { attachmentArr };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.productAttachment,
      attachments
    );
  }

  deleteServiceAttachment(attachmentArr: number[]): Promise<any> {
    const attachments = { attachmentArr };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.ServiceAttachment,
      attachments
    );
  }

  deleteServiceCertificate(certificateattachmentArr: number[]): Promise<any> {
    const attachments = { certificateattachmentArr };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.DeleteServiceCertificate,
      attachments
    );
  }

  fetchServiceCertificateById(certificateId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchServiceCertificateById}/${certificateId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getProductsForSupplier(id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.productsBySupplier}+/${id}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  // getSupplierData():Promise<any>{
  //   let apiUrl = `${ApiUrlConstants.getSupplierData}`
  //   return this.webApiService.getRequest<any>(apiUrl)
  // }

  addCatalogueProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.catalogueProductOp,
      reqBody
    );
  }

  updateCatalogueProduct(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.catalogueProductOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  updateSupplierBranch(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.supplierBranchOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  createSupplierBranch(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.supplierBranchOp,
      reqBody
    );
  }

  createVessel(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addUpdateVessel,
      reqBody
    );
  }

  importVessels(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importVessels,
      reqBody
    );
  }

  fetchVesselDataById(
    userId: number,
    columnName: string,
    columnValue: string
  ): Promise<any> {
    const url = `${ApiUrlConstants.fetchVesselDataByUserId}/${userId}`;
    let params = new HttpParams()
      .set('columnName', columnName)
      .set('columnValue', columnValue);
    return this.webApiService.getRequest<any>(url, params);
  }

  fetchVesselFilterValues(userId: number, columnName: string): Promise<any> {
    const url = `${ApiUrlConstants.fetchVesselFilterValues}/${userId}/${columnName}`;
    return this.webApiService.getRequest<any>(url);
  }

  getSustainabilityData(supplierId: number): Promise<any> {
    let params = new HttpParams().set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.sustainabilityOp,
      params
    );
  }

  addOrganizationalPurpose(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.organizationalPurposeOp,
      reqBody
    );
  }

  updateOrganizationalPurpose(reqBody: any, supplierId: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.organizationalPurposeOp}/${supplierId}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteOrganizationalPurpose(supplierId: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.organizationalPurposeOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  uploadISOCertificateData(data: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.isoCertificationOp}`;
    return this.webApiService.postRequest<any>(apiUrl, data);
  }

  updateISOCertificateData(data: any, id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.isoCertificationOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, data);
  }

  deleteISOCertificateData(supplierId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.isoCertificationOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  addSustainabilityCommitment(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.sustainabilityCommitmentOp,
      reqBody
    );
  }
  importSustainableData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importSustainableData,
      reqBody
    );
  }

  updateSustainabilityCommitment(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.sustainabilityCommitmentOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteSustainabilityCommitment(
    supplierId: any,
    commitmentId: number
  ): Promise<any> {
    let params = new HttpParams().set('commitmentId', commitmentId);
    let apiUrl = `${ApiUrlConstants.sustainabilityCommitmentOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl, params);
  }

  uploadUNGCParticipationData(data: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.ungcParticipationOp}`;
    return this.webApiService.postRequest<any>(apiUrl, data);
  }

  updateUNGCParticipationData(data: any, id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.ungcParticipationOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, data);
  }

  deleteUNGCParticipationData(supplierId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.ungcParticipationOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  uploadAdditionalDocumentsData(data: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.additionalDocumentsOp}`;
    return this.webApiService.postRequest<any>(apiUrl, data);
  }

  updateAdditionalDocumentsData(data: any, id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.additionalDocumentsOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, data);
  }

  deleteAdditionalDocumentsData(supplierId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.additionalDocumentsOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  addRelevantLinks(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.relevantLinksOp,
      reqBody
    );
  }

  updateRelevantLinks(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.relevantLinksOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteRelevantLinks(linkId: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.relevantLinksOp}/${linkId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  async saveCompanyLogoUrl(_params: any, companyId: number) {
    let apiUrl = `/users/uploadCompanyLogo/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async updateCompanyLogoUrl(_params: any, logoId: number) {
    let apiUrl = `/users/updateCompanyLogo/${logoId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async saveCompanyProfileLogoUrl(_params: any, companyId: number) {
    let apiUrl = `/users/uploadCompanyProfileLogo/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async updateCompanyProfileLogoUrl(_params: any, logoId: number) {
    let apiUrl = `/users/updateCompanyProfileLogo/${logoId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async saveSupplierSummaryVideo(_params: any) {
    let apiUrl = '/users/uploadSummaryVideo';
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async updateSupplierSummaryVideo(_params: any, videoId: number) {
    let apiUrl = `/users/updateSummaryVideo/${videoId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateUserData(_params: any, userId: number) {
    let apiUrl = `/users/update/${userId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateBuyerUserData(_params: any, userId: number) {
    let apiUrl = `/users/updateBuyerUser/${userId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateCompanyAddress(_params: any, companyId: number) {
    let apiUrl = `/users/updateCompany/${companyId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateCompanyName(_params: any, companyId: number) {
    let apiUrl = `/users/updateCompanyName/${companyId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateGstNumber(_params: any, companyId: number) {
    let apiUrl = `/users/updateGstNumber/${companyId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async uploadMembershipData(_params: any) {
    let apiUrl = `/users/memberships`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async uploadCertificateData(_params: any) {
    let apiUrl = `/users/certificates`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async deleteCertificate(certificateId: any) {
    let apiUrl = `/company/certificate/${certificateId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }

  async uploadMembershipDataById(_params: any, companyId: number) {
    let apiUrl = `/users/memberships/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async uploadCertificateDataById(_params: any, companyId: number) {
    let apiUrl = `/users/certificates/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async deleteMembership(membershipId: any) {
    let apiUrl = `/company/membership/${membershipId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }

  async createPorts(array: any[]) {
    let apiUrl = `/port/bulkAdd`;
    // return false;
    return this.webApiService.postRequest(apiUrl, array);
  }

  async deletePort(portId: any) {
    let apiUrl = `/port/delete/${portId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }
  async deletePortByAdmin(portId: any):Promise<any> {
    let apiUrl = `/port/deletePortByAdmin/${portId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }

  getCertificatesList(): Promise<informationType[]> {
    return this.webApiService.getRequest<informationType[]>(
      ApiUrlConstants.getCertificates
    );
  }

  getCertificatesListbyId(companyId: number): Promise<informationType[]> {
    const url = `${ApiUrlConstants.getCertificates}/${companyId}`;
    return this.webApiService.getRequest<informationType[]>(url);
  }

  getMembershipList(): Promise<informationType[]> {
    return this.webApiService.getRequest<informationType[]>(
      ApiUrlConstants.getMemberships
    );
  }

  getMembershipListbyId(companyId: number): Promise<informationType[]> {
    const url = `${ApiUrlConstants.getMemberships}/${companyId}`;
    return this.webApiService.getRequest<informationType[]>(url);
  }

  getSearchKeywordProducts(
    searchKeyword: any,
    supplierId?: number,
    pageMetaData?: Page
  ): Observable<PagedData<supplierCatalogueProduct>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', searchKeyword)
      .set('supplierId', supplierId || 0);

    return this.webApiService
      .getPaginatedRequest<PagedData<supplierCatalogueProduct>>(
        ApiUrlConstants.catalogueProductOp,
        params
      )
      .pipe(
        map((paginedProducts) => {
          // Set page metadata for frontend table properties
          paginedProducts.page.totalElements = paginedProducts.count;
          paginedProducts.page.size = _pageMetadata.size;
          paginedProducts.page.totalPages = Math.floor(
            paginedProducts.count / _pageMetadata.size
          );
          paginedProducts.page.pageNumber = _pageMetadata.pageNumber;
          paginedProducts.page.serachParam = _pageMetadata.serachParam;

          return paginedProducts;
        })
      );
    // console.log(searchKeyword);
    // let apiUrl = `/settings/catalogue/products/${searchKeyword}`;
    // return this.webApiService.getRequest<any>(apiUrl);
  }

  getAllCurrenciesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<Currency>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<Currency>>(
        ApiUrlConstants.getAllCurrenciesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCurrency) => {
          // Set page metadata for frontend table properties
          paginedCurrency.page.totalElements = paginedCurrency.count;
          paginedCurrency.page.size = _pageMetadata.size;
          paginedCurrency.page.totalPages = Math.floor(
            paginedCurrency.count / _pageMetadata.size
          );
          paginedCurrency.page.pageNumber = _pageMetadata.pageNumber;
          paginedCurrency.page.serachParam = _pageMetadata.serachParam;

          return paginedCurrency;
        })
      );
  }

  getAllCurrenciesByFliterKeyValueByRfqToken(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<Currency>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService
      .getPaginatedRequestWithOptions<PagedData<Currency>>(
        ApiUrlConstants.getAllCurrenciesByFliterKeyValueByRfqToken,
        params,
        options
      )
      .pipe(
        map((paginedCurrency) => {
          paginedCurrency.page.totalElements = paginedCurrency.count;
          paginedCurrency.page.size = _pageMetadata.size;
          paginedCurrency.page.totalPages = Math.floor(
            paginedCurrency.count / _pageMetadata.size
          );
          paginedCurrency.page.pageNumber = _pageMetadata.pageNumber;
          paginedCurrency.page.serachParam = _pageMetadata.serachParam;
          return paginedCurrency;
        })
      );
  }

  getAllCategoriesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllCategoriesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllSubCategoriesByFliterKeyValue(
    _filterValue: any,
    parentId: number,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false)
      .set('id', parentId);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllSubCategoriesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  
  getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierId(
    _filterValue: any,
    supplierId: number,
    pageMetaData?: Page,
    impa?: boolean,
  ): Observable<PagedData<AutoCompleteSupplierProduct>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set("supplierId",supplierId)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteSupplierProduct>>(
        ApiUrlConstants.getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierId,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierIdByRfqToken(
    _filterValue: any,
    supplierId: number,
    pageMetaData?: Page,
    impa?: boolean,
  ): Observable<PagedData<AutoCompleteSupplierProduct>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set("supplierId",supplierId)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService
      .getPaginatedRequestWithOptions<PagedData<AutoCompleteSupplierProduct>>(
        ApiUrlConstants.getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierIdByRfqToken,
        params,
        options
      )
      .pipe(
        map((paginedCategory) => {
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllServiceCategoriesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllServiceCategoriesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllUomByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllUomByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllBrandsByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<Brand>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllBrandsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedBrand) => {
          // Set page metadata for frontend table properties
          paginedBrand.page.totalElements = paginedBrand.count;
          paginedBrand.page.size = _pageMetadata.size;
          paginedBrand.page.totalPages = Math.floor(
            paginedBrand.count / _pageMetadata.size
          );
          paginedBrand.page.pageNumber = _pageMetadata.pageNumber;
          paginedBrand.page.serachParam = _pageMetadata.serachParam;

          return paginedBrand;
        })
      );
  }

  getAllAutoCompleteVesselsByFliterValue(
    _filterValue: any,
    buyerId: number,
    pageMetaData?: Page
  ): Observable<PagedData<Vessel>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('buyerId', buyerId)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<Vessel>>(
        ApiUrlConstants.getAllAutoCompleteVesselsByFliterValue,
        params
      )
      .pipe(
        map((paginedCurrency) => {
          // Set page metadata for frontend table properties
          paginedCurrency.page.totalElements = paginedCurrency.count;
          paginedCurrency.page.size = _pageMetadata.size;
          paginedCurrency.page.totalPages = Math.floor(
            paginedCurrency.count / _pageMetadata.size
          );
          paginedCurrency.page.pageNumber = _pageMetadata.pageNumber;
          paginedCurrency.page.serachParam = _pageMetadata.serachParam;

          return paginedCurrency;
        })
      );
  }

  getAllCountriesByFliterKeyValue(): Observable<
    PagedData<AutoCompleteDataType>
  > {
    let params = new HttpParams();
    // .set(PageParams.limit, _pageMetadata.size)
    // .set(PageParams.page, _pageMetadata.pageNumber + 1)
    // .set("cou", _filterValue)

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllCountriesByFilterKeyValue,
        params
      )
      .pipe(
        map((paginatedCountries) => {
          return paginatedCountries;
        })
      );
  }

  getAllRegionsByFliterKeyValue(
    _countryCode: string
  ): Observable<PagedData<AutoCompleteDataType>> {
    let params = new HttpParams().set('countryCode', _countryCode);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllRegionsByFilterKeyValue,
        params
      )
      .pipe(
        map((paginedRegions) => {
          return paginedRegions;
        })
      );
  }

  getAllCitiesByFliterKeyValue(
    _stateCode: string,
    _countryCode: string
  ): Observable<PagedData<AutoCompleteDataType>> {
    let params = new HttpParams()
      .set('countryCode', _countryCode)
      .set('stateCode', _stateCode);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllCitiesByFilterKeyValue,
        params
      )
      .pipe(
        map((paginedCities) => {
          return paginedCities;
        })
      );
  }

  getAllCountryRegionCities() {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllCountryRegionCities
    );
  }

  getAllMtmlUomByFliterKeyValue(
    _uomId: number
  ): Observable<PagedData<MtmlUom>> {
    let params = new HttpParams().set('uomId', _uomId);

    return this.webApiService
      .getPaginatedRequest<PagedData<MtmlUom>>(
        ApiUrlConstants.getAllMtmlUomByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCities) => {
          return paginedCities;
        })
      );
  }

  getAllServiceApprovalClassByFliterValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<ServiceApprovalClass>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<ServiceApprovalClass>>(
        ApiUrlConstants.getAllServiceApprovalClassByFliterValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllMembershipsByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllMembershipsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedMemberships) => {
          // Set page metadata for frontend table properties
          paginatedMemberships.page.totalElements = paginatedMemberships.count;
          paginatedMemberships.page.size = _pageMetadata.size;
          paginatedMemberships.page.totalPages = Math.floor(
            paginatedMemberships.count / _pageMetadata.size
          );
          paginatedMemberships.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMemberships.page.serachParam = _pageMetadata.serachParam;

          return paginatedMemberships;
        })
      );
  }

  getAllCertificatesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllCertificatesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedCertificates) => {
          // Set page metadata for frontend table properties
          paginatedCertificates.page.totalElements =
            paginatedCertificates.count;
          paginatedCertificates.page.size = _pageMetadata.size;
          paginatedCertificates.page.totalPages = Math.floor(
            paginatedCertificates.count / _pageMetadata.size
          );
          paginatedCertificates.page.pageNumber = _pageMetadata.pageNumber;
          paginatedCertificates.page.serachParam = _pageMetadata.serachParam;

          return paginatedCertificates;
        })
      );
  }

  getAllPortsByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllPortsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedPorts) => {
          // Set page metadata for frontend table properties
          paginatedPorts.page.totalElements = paginatedPorts.count;
          paginatedPorts.page.size = _pageMetadata.size;
          paginatedPorts.page.totalPages = Math.floor(
            paginatedPorts.count / _pageMetadata.size
          );
          paginatedPorts.page.pageNumber = _pageMetadata.pageNumber;
          paginatedPorts.page.serachParam = _pageMetadata.serachParam;

          return paginatedPorts;
        })
      );
  }

  getAllSDGCommitmentsByFliterKeyValue(
    _filterValue: any,
    _commitmentsDeclared: any[],
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('_commitmentsDeclared', JSON.stringify(_commitmentsDeclared));

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllSDGCommitmentsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedCommitments) => {
          // Set page metadata for frontend table properties
          paginatedCommitments.page.totalElements = paginatedCommitments.count;
          paginatedCommitments.page.size = _pageMetadata.size;
          paginatedCommitments.page.totalPages = Math.floor(
            paginatedCommitments.count / _pageMetadata.size
          );
          paginatedCommitments.page.pageNumber = _pageMetadata.pageNumber;
          paginatedCommitments.page.serachParam = _pageMetadata.serachParam;
          console.log('returning sdg commitments==>', paginatedCommitments);
          return paginatedCommitments;
        })
      );
  }

  getAllVesselFilterValuesByFliterKeyValue(
    _filterValue: any,
    filterKey: any,
    userId: number,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteVesselFilterDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterKey', filterKey)
      .set('filterValue', _filterValue)
      .set('userId', userId);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteVesselFilterDataType>>(
        ApiUrlConstants.getAllAutocompleteVesselFilterData,
        params
      )
      .pipe(
        map((paginatedPorts) => {
          // Set page metadata for frontend table properties
          paginatedPorts.page.totalElements = paginatedPorts.count;
          paginatedPorts.page.size = _pageMetadata.size;
          paginatedPorts.page.totalPages = Math.floor(
            paginatedPorts.count / _pageMetadata.size
          );
          paginatedPorts.page.pageNumber = _pageMetadata.pageNumber;
          paginatedPorts.page.serachParam = _pageMetadata.serachParam;

          return paginatedPorts;
        })
      );
  }

  async downloadS3FileAsBlob(
    url: string,
    fileName: string = 'fileName.ext'
  ): Promise<File> {
    const blob: any = await this.webApiService.getS3FileAsBlob(url);
    return new File([blob], fileName, { type: blob.type });
  }

  getImpaData(): Promise<any> {
    return this.webApiService.getRequest<any>(ApiUrlConstants.getImpaData);
  }

  getImpaDataById(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.supplierImpaproduct,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          return paginatedMaster;
        })
      );
  }

  // service api
  getServiceData(): Promise<any> {
    return this.webApiService.getRequest<any>(ApiUrlConstants.getServiceData);
  }

  getPortsByAdmin(
    searchParam?: string,
    sortDirection?: string,
    sortField?: string,
    limit: number = 10,
    offset: number = 0,
    isActive:number=1,
    isDelete:number=0
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getPortsByAdmin,
      {
        searchParam,
        sortDirection,
        sortField,
        limit,
        offset,
        isActive,
        isDelete
      }
    );
  }       
  getUomMtmlUomData(
    searchParam?: string,
    sortDirection?: string,
    sortField?: string,
    limit: number = 10,
    offset: number = 0,
    isActive:number=1,
    isDelete:number=0,
    
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getUomMtmlUomData,
      {
        searchParam,
        sortDirection,
        sortField,
        limit,
        offset,
        isActive,
        isDelete
        
      }
    );
  } 
  exportPortsByAdmin(): Promise<any> {
    return this.webApiService.getRequest<any>(ApiUrlConstants.exportPortsByAdmin);
  }

  exportUomMtmlUomData(): Promise<any> {
    return this.webApiService.getRequest<any>(ApiUrlConstants.exportUomMtmlUomData);
  }

  getServiceDataById(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getSupplierServiceData,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          return paginatedMaster;
        })
      );
  }

  getAllServiceData(
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getAllServiceData,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          return paginatedMaster;
        })
      );
  }

  addService(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createService,
      reqBody
    );
  }

  addServiceCertificate(reqBody: any, serviceId: number): Promise<any> {
    const url = `${ApiUrlConstants.createServiceCertificate}/${serviceId}`;
    return this.webApiService.postRequest<any>(url, reqBody);
  }

  deleteService(serviceId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.getServiceData}/${serviceId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  updateService(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.createService}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  updateServiceCertificate(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.updateServiceCertificateById}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }
  // service api ends

  getBrandCategory(params: string, supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.getBrandCategory}/${params}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getSearchSupplierEmail(
    index: number,
    searchParam: any,
    userCategory: any
  ): Promise<any> {
    // console.log(searchKeyword);
    let apiUrl = `/search/supplierLists/validate?index=${index}&searchParam=${searchParam}&userCategory=${userCategory}`;

    return this.webApiService.getRequest<any>(apiUrl);
  }

  addCompanyProductServiceDescription(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addCompanyProductServiceDescription,
      reqBody
    );
  }

  getProductServiceDescriptionById(companyId: number): Promise<any> {
    const url = `${ApiUrlConstants.getProductServiceDescription}/${companyId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getCatalogueDataById(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page,
    type: string
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId)
      .set('type', type);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getCatalogueDataById,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          console.log(paginatedMaster.count, _pageMetadata.size);
          return paginatedMaster;
        })
      );
  }

  addCatalogue(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.CreateCatalogue,
      reqBody
    );
  }

  deleteCatalogue(catalogueId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.getCatalogueDataById}/${catalogueId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  updateCatalogue(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.CreateCatalogue}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  getSearchImpaItems(searchTerm: string, itemType: string) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('itemType', itemType);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchImpaItems,
      params
    );
  }

  getSearchImpaItemsMySupplier(searchTerm: string, itemType: string, userId: number) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('itemType', itemType)
      .set('userId', userId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchImpaItemsMySupplier,
      params
    );
  }

  getSearchCountryPorts(searchTerm: string, itemType: string) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('itemType', itemType);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getSearchCountryPorts,
      params
    );
  }

  getSupplierProfileData(supplierId: any): Promise<any> {
    const params = { supplierId };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getSupplierProfileData,
      params
    );
  }

  getLimitedProfileData(params: string, supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.getLimitedProfileData}/${params}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getSearchCategoryItems(searchTerm: string, supplierId: any) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchCategoryItems,
      params
    );
  }

  getSearchBrandItems(searchTerm: string, supplierId: any) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchBrandItems,
      params
    );
  }

  getArchivedItems(getFor: string, supplierId: number): Promise<any> {
    let params = new HttpParams()
      .set('getFor', getFor)
      .set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getArchivedItems,
      params
    );
  }

  getDetailedArchivedItems(
    getFor: string,
    supplierId: number,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set('getFor', getFor)
      .set('supplierId', supplierId)
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1);

    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getDetailedArchivedItems,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;

          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages,
            _pageMetadata.size,
            paginatedMaster.count
          );
          // console.log('PaginatedMaster Data-> ',paginatedMaster);
          return paginatedMaster;
        })
      );
  }

  restoreArchivedItems(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.restoreArchivedItems,
      params
    );
  }

  inviteUserBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.inviteUserBySupplier,
      reqBody
    );
  }

  updateUserDetails(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateUserDetails,
      reqBody
    );
  }


  fetchUserBySupplierId(
    supplierId: number,
    limit: number,
    offset: number,
    searchParam: string = ''
  ): Observable<{ rows: any[]; totalElements: number }> {
    const apiUrl = `${
      ApiUrlConstants.fetchUserBySupplier
    }?supplierId=${supplierId}&limit=${limit}&offset=${offset}&searchParam=${encodeURIComponent(
      searchParam
    )}`;

    console.log('parms', offset);
    return from(
      this.webApiService.getRequest1<{ rows: any[]; totalElements: number }>(
        apiUrl
      )
    );
  }

  getImpaDetailsforRfq(impaId: number): Promise<any> {
    const url = `${ApiUrlConstants.getImpaDetailsforRfq}/${impaId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getCardData(obj: any): Promise<any> {
    const name = obj.name;

    const type = obj.type;

    const url = `/settings/getCardData/${name}/${type}`;

    return this.webApiService.getCardDataRequest<any>(url);
  }

  getSupplierCount(catName: any): Promise<any> {
    const url = `${ApiUrlConstants.getSupplierCount}/${catName}`;

    return this.webApiService.getRequest<any>(url);
  }

  getSupplierCountByBrand(brandName: string): Promise<any> {
    const url = `${ApiUrlConstants.getSupplierCountByBrand}/${brandName}`;

    return this.webApiService.getRequest<any>(url);
  }

  getSupplierCountByServiceCategory(catName: any): Promise<any> {
    const url = `${ApiUrlConstants.getSupplierCountByServiceCategory}/${catName}`;

    return this.webApiService.getRequest<any>(url);
  }

  fetchSetupEmailsByUserId(userId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchSetupEmailsByUserId}/${userId}`;
    return this.webApiService.getRequest<any>(url);
  }

  addRecentSearchHistory(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addRecentSearchHistory,
      reqBody
    );
  }

  fetchUserSearchHistory(userId?: number, sessionId?: string): Promise<any> {
    let params = new HttpParams()
      .set('userId', userId || '')
      .set('sessionId', sessionId || '');
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.fetchUserSearchHistory,
      params
    );
  }

  createSupplierSetup(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createSupplierSetup,
      reqBody
    );
  }

  fetchSupplierSetup(section: string, supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchSupplierSetup}/${section}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchSupplierSetupByRfqToken(section: string): Promise<any> {
    const url = `${ApiUrlConstants.fetchSupplierSetupByRfqToken}/${section}`;
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.getRequestWithOptions<any>(url, options);
  }

  fetchBuyerNotificationSettings(buyerId: number): Promise<any> {
    const url = `${ApiUrlConstants.getBuyerNotificationSettings}/${buyerId}`;
    return this.webApiService.getRequest<any>(url);
  }

  createBuyerNotificationSettings(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createBuyerNotificationSettings,
      reqBody
    );
  }

  updateBuyerNotificationSettings(buyerId: number, reqBody: any): Promise<any> {
    const url = `${ApiUrlConstants.updateBuyerNotificationSettings}/${buyerId}`;
    return this.webApiService.putRequest<any>(url, reqBody);
  }

  // Supplier notification settings (dedicated endpoints)
  fetchSupplierNotificationSettings(supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchSupplierNotificationSettings}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  createSupplierNotificationSettings(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createSupplierNotificationSettings,
      reqBody
    );
  }

  updateSupplierNotificationSettings(supplierId: number, reqBody: any): Promise<any> {
    const url = `${ApiUrlConstants.updateSupplierNotificationSettings}/${supplierId}`;
    return this.webApiService.putRequest<any>(url, reqBody);
  }

  UpdateUserSearchHistory(id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.UpdateUserSearchHistory}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, {});
  }

  fetchUserInbox(supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchUserInbox}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchBranchAddressFilterValues(
    buyerId: number,
    addressType: number
  ): Promise<any> {
    const url = `${ApiUrlConstants.fetchBranchAddressFilterValues}/${buyerId}/${addressType}`;
    return this.webApiService.getRequest<any>(url);
  }

  getUnreadMailCountByUserId() {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getUnreadMailCount
    );
  }

  markAsRead(mailId: number) {
    const url = `${ApiUrlConstants.markAsRead}/${mailId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllRfqBySupplierId(
    filterValue: string,
    supplierId: number,
    // docStatus: string,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllRfqBySupplierId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        supplierId,
        docStatus,
        fromDate,
        toDate,
        isActive,
      }
    );
  }
  fetchQuoteDetailsBySupplierId(
    filterValue: string,
    supplierId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    if (!limit) limit = 10;
    if (!offset) offset = 0;
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchQuoteDetailsBySupplierId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        supplierId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  fetchAllRfqBybuyerId(
    filterValue: string,
    buyerId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllRfqBybuyerId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        buyerId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  fetchSupplierUserByRfqId(rfqId: number): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchSupplierUserByRfqId,
      {
        rfqId,
      }
    );
  }
  assignRfqSupplierUser(
    rfqIds: any,
    supplierId: number,
    supplierUserId: number
  ): Promise<any> {
    console.log({ rfqIds });
    console.log({ supplierId });
    console.log({ supplierUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignSupplierUser,
      {
        rfqIds,
        supplierId,
        supplierUserId,
      }
    );
  }
  
  assignRFQToBuyerUser(
    rfqIds: any,
    buyerId: number,
    buyerUserId: number
  ): Promise<any> {
    console.log({ rfqIds });
    console.log({ buyerId });
    console.log({ buyerUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignRFQToBuyerUser,
      {
        rfqIds,
        buyerId,
        buyerUserId,
      }
    );
  }

  assignPOToBuyerUser(
    poIds: any,
    buyerId: number,
    buyerUserId: number
  ): Promise<any> {
    console.log({ poIds });
    console.log({ buyerId });
    console.log({ buyerUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignPOToBuyerUser,
      {
        poIds,
        buyerId,
        buyerUserId,
      }
    );
  }

  assignPOToSupplierUser(
    poIds: any,
    supplierId: number,
    supplierUserId: number
  ): Promise<any> {
    console.log({ poIds });
    console.log({ supplierId });
    console.log({ supplierUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignPOToSupplierUser,
      {
        poIds,
        supplierId,
        supplierUserId,
      }
    );
  }

        
  assignQuoteToSupplierUser(
    quoteIds: any,
    supplierId: number,
    supplierUserId: number
  ): Promise<any> {
    console.log({ quoteIds });
    console.log({ supplierId });
    console.log({ supplierUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignQuoteToSupplierUser,
      {
        quoteIds,
        supplierId,
        supplierUserId,
      }
    );
  }

  async generateSupplierRfqAttachment(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateSupplierRfqAttachment,
      params
    );
  }

  async generateSupplierRfqAttachmentByRfqToken(params: any) {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateSupplierRfqAttachmentByRfqToken,
      params,
      options
    );
  }

  rfqOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.rfqOperations,
      params
    );
  }
  rfqOperationsByRfqToken(params: any): Promise<any> {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.rfqOperationsByRfqToken,
      params,
      options
    );
  }
  buyerRfqOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.buyerRfqOperations,
      params
    );
  }

  sendRfqRemindersByBuyer(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.sendRfqRemindersByBuyer,
      reqBody
    );
  }

  quoteOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.quoteOperations,
      params
    );
  }

  addPortAgent(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPortAgent,
      reqBody
    );
  }

  addFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addFreightForwarder,
      reqBody
    );
  }

  importPortAgent(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importPortAgent,
      reqBody
    );
  }

  importFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importFreightForwarder,
      reqBody
    );
  }

  fetchPortAgentByUserId(userId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchPortAgentByUserId}/${userId}`;
    return this.webApiService.getRequest1<any>(url);
  }

  fetchFreightForwarderByUserId(userId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchFreightForwarderByUserId}/${userId}`;
    return this.webApiService.getRequest1<any>(url);
  }

  // on Suppllier's User page
  importUsersBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importUsers,
      reqBody
    );
  }
  fetchAllUsersBySupplierId(supplierId: number): Observable<{ rows: any[] }> {
    const apiUrl = `${ApiUrlConstants.exportAllUsers}?supplierId=${supplierId}`;
    return from(this.webApiService.getRequest1<{ rows: any[] }>(apiUrl));
  }

  importProductsByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importProducts,
      reqBody
    );
  }
  importProductsByBuyer(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importProductsByBuyer,
      reqBody
    );
  }
  importAllImpaDataBySupplierId(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importAllImpaDataBySupplierId,
      reqBody
    );
  }
  importServicesByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServicesByAdmin,
      reqBody
    );
  }

  importServicesByBuyer(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServicesByBuyer,
      reqBody
    );
  }
  importServicesBySupplierId(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServicesBySupplierId,
      reqBody
    );
  }

  exportAllProductsByAdmin(): Observable<{ rows: any[] }> {
    // let apiUrl = `/products/exportAllProducstByAdmin`;
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportProducts
      )
    );
  }

  exportAllProductsByBuyer(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllProductsByBuyer
      )
    );
  }

  exportAllImpaDataBySupplierId(
    category: boolean,
    isImpa: boolean,
    supplierId: number
  ): Observable<{ rows: any[] }> {
    const params = new HttpParams()
      .set('category', String(category))
      .set('isImpa', String(isImpa))
      .set('supplierId', String(supplierId));
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllImpaDataBySupplierId,
        params
      )
    );
  }
  exportAllServicesByAdmin(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllServicesByAdmin
      )
    );
  }

  exportAllServicesByBuyer(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllServicesByBuyer
      )
    );
  }

  exportServicesBySupplierId(supplierId: number): Observable<{ rows: any[] }> {
    const params = new HttpParams().set('supplierId', String(supplierId));
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportServicesBySupplierId,
        params
      )
    );
  }

  importSuppliersBranches(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importSuppliersBranches,
      reqBody
    );
  }

  exportAllCategoriesByAdmin(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllCategoriesByAdmin
      )
    );
  }

  exportAllBrandsByAdmin(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllBrandsByAdmin
      )
    );
  }
  exportAllServiceCategoriesByAdmin(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllServiceCategoriesByAdmin
      )
    );
  }
  importCategoriesByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importCategoriesByAdmin,
      reqBody
    );
  }
  importBrandsByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importBrandsByAdmin,
      reqBody
    );
  }

  importServiceCategoriesByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServiceCategoriesByAdmin,
      reqBody
    );
  }


  addTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addTaxDetailsBySupplier,
      reqBody
    );
  } 

  importTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importTaxDetailsBySupplier,
      reqBody
    );
  }

  updateTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateTaxDetailsBySupplier,
      reqBody
    );
  }
  getTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getTaxDetailsBySupplier,
      reqBody
    );
  }
  getTaxDetailsBySupplierByPoToken(reqBody: any): Promise<any> {
    const poId = this.poTokenAuth.getPoId();
    const body = {
      ...reqBody,
      ...(poId && !reqBody?.purchaseOrderId ? { purchaseOrderId: poId } : {})
    };
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getTaxDetailsBySupplierByPoToken,
      body,
      options
    );
  }

  getTaxDetailsBySupplierByRfqToken(reqBody: any): Promise<any> {
    const rfqId = this.rfqTokenAuth.getRfqId();
    const body = {
      ...reqBody,
      ...(rfqId && !reqBody?.rfqId ? { rfqId: rfqId } : {})
    };
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getTaxDetailsBySupplierByRfqToken,
      body,
      options
    );
  }

  fetchAllQuotesByBuyerId(
    filterValue: string,
    buyerId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    if (!limit) limit = 10;
    if (!offset) offset = 0;
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllQuotesByBuyerId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        buyerId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  fetchQuoteLineItemDetailsByQuoteId(
    id: number
  ): Promise<any> {
   
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchQuoteLineItemDetailsByQuoteId,
      {
        id
      }
    );
  }

  async generateQuoteAttachment(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateQuoteAttachment,
      params
    );
  }

  buyerQuoteOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.quoteOperationsByBUyer,
      params
    );
  }

  async generateRfqAttachmentForBuyer(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateRfqAttachmentForBuyer,
      params
    );
  }

  assignPOApprovalToBuyerUser(
    poIds: any,
    buyerId: number,
    buyerUserId: number
  ): Promise<any> {
    console.log({ poIds });
    console.log({ buyerId });
    console.log({ buyerUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignPOApprovalToBuyerUser,
      {
        poIds,
        buyerId,
        buyerUserId,
      }
    );
  }

  fetchAllDispatchRequirements(): Promise<any> {
    let apiUrl = `/settings/fetchAllDispatchRequirements`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  createDispatchRequirement(reqBody: any): Promise<any> {
    let apiUrl = `/settings/createDispatchRequirement`;
    return this.webApiService.postRequest<any>(
      apiUrl,
      reqBody
    );
  }


  async fetchSupplierRfqAttachmentExcelData(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchSupplierRfqAttachmentExcelData,
      params
    );
  }

  async fetchBuyerRfqAttachmentExcelData(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchBuyerRfqAttachmentExcelData,
      params
    );
  }

  async fetchQuoteAttachmentExcelData(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchQuoteAttachmentExcelData,
      params
    );
  }

  async fetchQuoteExcelTemplateData(params: any) {
    let apiUrl = '/quote/fetchQuoteExcelTemplateData'
    return this.webApiService.postRequest<any>(
      apiUrl,
      params
    );
  }
}
